/**
 * MobX store for gap/silence removal state.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { GapOccurrence } from './gap';
import { V2TranscriptWord } from '../V2TextEditor';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ProjectAssetsStore } from '../../../state/ProjectAssetsStore';
import { isScriptClip, ScriptLayerClip } from '../../../engine/clips';
import { readMonoWindowFromSink, rms } from '../../../engine/clips/utils/cut-analysis';

// Type for the GetTranscript function from useAssetStore hook
type GetTranscriptFn = (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;

class GapStoreImpl {
    // Whether gap removal mode is active
    isActive: boolean = false;
    
    // Current gaps found
    gaps: GapOccurrence[] = [];
    
    // Currently focused gap index
    currentIndex: number = 0;
    
    // Minimum gap threshold in seconds (only show gaps longer than this)
    minGapThreshold: number = 3.0;
    
    // Target duration to shorten gaps to
    shortenToSeconds: number = 1.0;
    
    // Whether removal is in progress
    isRemoving: boolean = false;
    
    // Progress of removal (0-100)
    removalProgress: number = 0;
    
    // All transcript words (for reference)
    private allWords: V2TranscriptWord[] = [];
    
    // Reference to script clip for audio analysis
    private scriptClip: ScriptLayerClip | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /**
     * Set the minimum gap threshold.
     */
    async setMinGapThreshold(seconds: number) {
        this.minGapThreshold = Math.max(0, seconds);
        if (this.isActive) {
            if (this.scriptClip) {
                await this.scanForGapsAsync(this.scriptClip);
            } else {
                this.scanForGaps();
            }
        }
    }
    
    /**
     * Set the target duration to shorten gaps to.
     */
    setShortenToSeconds(seconds: number) {
        this.shortenToSeconds = Math.max(0, seconds);
    }

    /**
     * Activate gap removal mode by fetching words from the script clip.
     * @param getTranscript - The GetTranscript function from useAssetStore hook
     */
    async activateAsync(getTranscript: GetTranscriptFn) {
        // Get the script layer info
        const scriptLayerInfo = CompositionService.composition.scriptLayer;
        const scriptClipId = scriptLayerInfo?.clipId;

        if (!scriptClipId) {
            return;
        }

        const clip = CompositionService.getClipById(scriptClipId);
        if (!clip || !isScriptClip(clip)) {
            return;
        }
        
        const scriptClip = clip as ScriptLayerClip;
        const audioEntries = scriptClip.getAudioEntries();
        const projectId = ProjectAssetsStore.projectId as string;
        
        // Use derived segments to filter out words that are no longer "visible/playable"
        // (e.g., removed by cuts or replaced with gaps).
        // NOTE: These times are in script-clip local time (the same domain as WordNode.__payload.startSec/endSec).
        const scriptSegments = (scriptClip as any).scriptSegments;
        const segments: Array<{ srcStartSec: number; srcEndSec: number; isGap?: boolean }> =
            Array.isArray(scriptSegments) && scriptSegments.length > 0
                ? scriptSegments
                : (Array.isArray((scriptClip as any).segments) ? (scriptClip as any).segments : []);

        // Helper to check if a word (by clip-local time) falls within a non-gap segment
        const isWordInPlayableSection = (clipStart: number, clipEnd: number): boolean => {
            if (segments.length === 0) return true; // No segments means no cuts
            
            for (const seg of segments) {
                if (seg.isGap) continue;
                if (clipStart >= seg.srcStartSec && clipEnd <= seg.srcEndSec) {
                    return true;
                }
            }
            return false;
        };
        
        const allWords: V2TranscriptWord[] = [];
        const MIN_ENERGY_RATIO = 0.2;
        const toFiniteNumber = (value: any, fallback: number): number => {
            const n = Number(value);
            return Number.isFinite(n) ? n : fallback;
        };
        
        // Fetch transcripts for each audio source
        for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
            const entry = audioEntries[sourceIndex];
            const assetId = entry.assetId;
            if (!assetId) continue;
            
            const range = entry.range;
            const offsetSec = entry.offsetSec ?? 0;
            const playbackRateRaw = Number((entry as any).playbackRate ?? 1);
            const playbackRate = Number.isFinite(playbackRateRaw) && playbackRateRaw > 0 ? playbackRateRaw : 1;
            
            // Get source duration for range defaults
            const trackRecord = ProjectAssetsStore.projectTracks.find(
                (t: any) => t.id === assetId || t.trackId === assetId
            );
            const sourceDuration = trackRecord?.duration ?? 0;
            const rangeInSec = range?.inSec ?? 0;
            const rangeOutSec = range?.outSec ?? (sourceDuration > 0 ? sourceDuration : Infinity);
            
            try {
                // Get transcriptId from track record
                const transcriptId = trackRecord?.activeTranscriptId;
                if (!transcriptId) {
                    continue;
                }
                const transcriptJson = await getTranscript({ transcriptId, projectId });

                if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) {
                    continue;
                }
                
                // Convert and filter words
                for (const w of transcriptJson.words) {
                    if ((w.type ?? 'word') !== 'word') continue;

                    // Filter out faint words if energy data is present
                    if (w.energy?.ratio != null && w.energy.ratio > 0 && w.energy.ratio < MIN_ENERGY_RATIO) {
                        continue;
                    }
                    
                    // Support both t0/t1 (new) and start/end (legacy) field names
                    const wordStartSrc = toFiniteNumber(w.t0 ?? w.start, 0);
                    const wordEndSrc = toFiniteNumber(w.t1 ?? w.end, wordStartSrc);
                    
                    // Only include words within the source range
                    if (wordStartSrc < rangeInSec || wordEndSrc > rangeOutSec) continue;
                    
                    // Map source transcript time → script-clip local time.
                    // clipLocal = offsetSec + (srcTime - rangeInSec) / playbackRate
                    const wordStart = offsetSec + (wordStartSrc - rangeInSec) / playbackRate;
                    const wordEnd = offsetSec + (wordEndSrc - rangeInSec) / playbackRate;
                    
                    // Check if word falls within a playable (non-cut) section
                    if (!isWordInPlayableSection(wordStart, wordEnd)) {
                        continue;
                    }
                    
                    allWords.push({
                        text: String(w.text ?? w.word ?? '').trim(),
                        // Script-clip local time (stable across cuts; used for selection + cutting)
                        startSec: wordStart,
                        endSec: wordEnd,
                        // Original source times (for audio analysis)
                        srcStartSec: wordStartSrc,
                        srcEndSec: wordEndSrc,
                        sourceIndex,
                    });
                }
            } catch (err) {
                // Skip failed transcript fetch
            }
        }

        // Sort by time
        allWords.sort((a, b) => a.startSec - b.startSec);

        runInAction(() => {
            this.isActive = true;
            this.allWords = allWords;
            this.scriptClip = scriptClip;
        });
        
        // Use async scan with audio analysis for detecting silence in long words
        await this.scanForGapsAsync(scriptClip);
    }
    
    /**
     * Deactivate gap removal mode.
     */
    deactivate() {
        this.isActive = false;
        this.gaps = [];
        this.currentIndex = 0;
        this.allWords = [];
        this.isRemoving = false;
        this.removalProgress = 0;
        this.scriptClip = null;
    }
    
    /**
     * Scan for gaps between words that exceed the threshold.
     * Also detects suspiciously long words where silence may be embedded in the word duration.
     * For long words, uses audio analysis to find where silence actually starts.
     */
    private async scanForGapsAsync(scriptClip: ScriptLayerClip) {
        const words = this.allWords;
        const newGaps: GapOccurrence[] = [];

        // For V1 gap shortening we only want to operate on "simple" gaps that exist within
        // a single non-gap segment of the script clip. This avoids spanning cut boundaries
        // (jump cuts) or intentionally inserted time (gapInsertions), which can otherwise
        // produce unsafe cut ranges.
        const segs: Array<{ srcStartSec: number; srcEndSec: number; isGap?: boolean }> =
            Array.isArray((scriptClip as any).scriptSegments)
                ? (scriptClip as any).scriptSegments
                : (Array.isArray((scriptClip as any).segments) ? (scriptClip as any).segments : []);
        const EPS = 1e-3;
        const findNonGapSegmentIndex = (timeSec: number): number | null => {
            if (!Number.isFinite(timeSec) || !segs.length) return null;
            for (let idx = 0; idx < segs.length; idx++) {
                const seg = segs[idx];
                if (seg.isGap) continue;
                if (timeSec >= seg.srcStartSec - EPS && timeSec < seg.srcEndSec - EPS) {
                    return idx;
                }
                // Handle exact end mapping (e.g., word at the very end of the clip).
                if (Math.abs(timeSec - seg.srcEndSec) <= EPS) {
                    return idx;
                }
            }
            return null;
        };
        
        // Threshold for "suspiciously long" words that need audio analysis
        const SUSPICIOUS_WORD_DURATION_SEC = 3.0;

        // RMS threshold for silence detection (amplitude below this is considered silence)
        const SILENCE_RMS_THRESHOLD = 0.02;

        // Chunk size for audio analysis (analyze in 100ms windows)
        const ANALYSIS_CHUNK_SEC = 0.1;

        // Minimum consecutive silent chunks to consider it a silence region
        const MIN_SILENT_CHUNKS = 3; // 300ms of consecutive silence
        
        for (let i = 0; i < words.length; i++) {
            const currentWord = words[i];
            const currentStart = currentWord.startSec;
            const currentEnd = currentWord.endSec ?? currentWord.startSec;
            const wordDuration = currentEnd - currentStart;
            
            // === CHECK 1: Suspiciously long word (embedded silence) ===
            // Use audio analysis to find where silence actually starts
            if (wordDuration > SUSPICIOUS_WORD_DURATION_SEC) {
                const sourceIndex = currentWord.sourceIndex ?? 0;
                const srcStart = currentWord.srcStartSec ?? 0;
                const srcEnd = currentWord.srcEndSec ?? srcStart;
                
                // Try to find where silence starts using audio analysis
                const silenceStartSec = await this.findSilenceStart(
                    scriptClip,
                    sourceIndex,
                    srcStart,
                    srcEnd,
                    ANALYSIS_CHUNK_SEC,
                    SILENCE_RMS_THRESHOLD,
                    MIN_SILENT_CHUNKS
                );
                
                if (silenceStartSec !== null) {
                    // We found silence! Convert to clip-local duration (accounts for playbackRate).
                    const entry = scriptClip.getAudioEntries?.()?.[sourceIndex];
                    const playbackRateRaw = Number(entry?.playbackRate ?? 1);
                    const playbackRate = Number.isFinite(playbackRateRaw) && playbackRateRaw > 0 ? playbackRateRaw : 1;
                    const silenceDuration = (srcEnd - silenceStartSec) / playbackRate;
                    
                    // Only include if silence exceeds threshold
                    if (silenceDuration > this.minGapThreshold) {
                        // Map silence start (source time) back to clip-local time.
                        const offsetFromStart = (silenceStartSec - srcStart) / playbackRate;
                        const gapStart = currentStart + offsetFromStart;
                        const gapEnd = currentEnd;
                        
                        // Get context
                        const CONTEXT_SECONDS = 2.0;
                        const nextWord = i < words.length - 1 ? words[i + 1] : null;
                        
                        const contextBefore = words
                            .filter((w, idx) => idx < i && w.startSec >= (currentStart - CONTEXT_SECONDS))
                            .map(w => w.text);
                        contextBefore.push(currentWord.text);
                        
                        const contextAfter = nextWord ? words
                            .filter((w, idx) => idx > i && w.startSec <= (currentEnd + CONTEXT_SECONDS))
                            .map(w => w.text) : [];
                        
                        const gap: GapOccurrence = {
                            id: `longword_${i}_${gapStart.toFixed(2)}`,
                            durationSec: silenceDuration,
                            startSec: gapStart,
                            endSec: gapEnd,
                            srcStartSec: silenceStartSec,
                            srcEndSec: srcEnd,
                            sourceIndex,
                            wordBefore: {
                                text: currentWord.text,
                                startSec: currentStart,
                                endSec: gapStart,
                            },
                            wordAfter: nextWord ? {
                                text: nextWord.text,
                                startSec: nextWord.startSec,
                                endSec: nextWord.endSec ?? nextWord.startSec,
                            } : null,
                            contextBefore,
                            contextAfter,
                        };
                        
                        newGaps.push(gap);
                    }
                }
            }
            
            // === CHECK 2: Gap between this word and next word ===
            if (i < words.length - 1) {
                const nextWord = words[i + 1];
                const nextStart = nextWord.startSec;

                // Only consider gaps where both words are within the same non-gap segment.
                // This prevents creating a single cut that spans a jump cut boundary.
                const currSegIndex = findNonGapSegmentIndex(currentWord.startSec);
                const nextSegIndex = findNonGapSegmentIndex(nextWord.startSec);
                if (currSegIndex === null || nextSegIndex === null || currSegIndex !== nextSegIndex) {
                    continue;
                }

                const gapDuration = nextStart - currentEnd;
                
                // Only include gaps longer than threshold
                if (gapDuration > this.minGapThreshold) {
                    // Get context (2 seconds before and after)
                    const CONTEXT_SECONDS = 2.0;
                    const contextStartTime = currentEnd - CONTEXT_SECONDS;
                    const contextEndTime = nextStart + CONTEXT_SECONDS;
                    
                    const contextBefore = words
                        .filter((w, idx) => idx <= i && w.startSec >= contextStartTime)
                        .map(w => w.text);
                    const contextAfter = words
                        .filter((w, idx) => idx > i && w.startSec <= contextEndTime)
                        .map(w => w.text);
                    
                    const gap: GapOccurrence = {
                        id: `gap_${i}_${currentEnd.toFixed(2)}`,
                        durationSec: gapDuration,
                        startSec: currentEnd,
                        endSec: nextStart,
                        srcStartSec: currentWord.srcEndSec ?? currentEnd,
                        srcEndSec: nextWord.srcStartSec ?? nextStart,
                        sourceIndex: currentWord.sourceIndex ?? 0,
                        wordBefore: {
                            text: currentWord.text,
                            startSec: currentWord.startSec,
                            endSec: currentEnd,
                        },
                        wordAfter: {
                            text: nextWord.text,
                            startSec: nextStart,
                            endSec: nextWord.endSec ?? nextStart,
                        },
                        contextBefore,
                        contextAfter,
                    };
                    
                    newGaps.push(gap);
                }
            }
        }
        
        // Sort by time
        newGaps.sort((a, b) => a.startSec - b.startSec);

        runInAction(() => {
            this.gaps = newGaps;
            this.currentIndex = newGaps.length > 0 ? 0 : -1;
        });
    }

    /**
     * Analyze audio to find where silence starts within a word.
     * Scans from start to end in chunks, looking for consecutive low-RMS regions.
     * 
     * @returns Source time where silence starts, or null if no silence detected
     */
    private async findSilenceStart(
        scriptClip: ScriptLayerClip,
        sourceIndex: number,
        srcStartSec: number,
        srcEndSec: number,
        chunkDurationSec: number,
        silenceThreshold: number,
        minSilentChunks: number
    ): Promise<number | null> {
        const sink = scriptClip.getAudioSinkForSource(sourceIndex);
        if (!sink) {
            return null;
        }
        
        const sampleRate = 48000; // Assume 48kHz
        const totalDuration = srcEndSec - srcStartSec;
        const numChunks = Math.floor(totalDuration / chunkDurationSec);
        
        if (numChunks < minSilentChunks) {
            return null; // Not enough chunks to analyze
        }
        
        let consecutiveSilentChunks = 0;
        let silenceStartTime: number | null = null;
        
        for (let c = 0; c < numChunks; c++) {
            const chunkStartSec = srcStartSec + c * chunkDurationSec;
            
            try {
                // Read audio samples for this chunk
                const samples = await readMonoWindowFromSink(sink, chunkStartSec, chunkDurationSec, sampleRate);
                
                if (!samples || samples.length === 0) {
                    // No data - treat as silence
                    if (consecutiveSilentChunks === 0) {
                        silenceStartTime = chunkStartSec;
                    }
                    consecutiveSilentChunks++;
                } else {
                    // Calculate RMS for this chunk (entire samples array)
                    const chunkRms = rms(samples, 0, samples.length);
                    
                    if (chunkRms < silenceThreshold) {
                        // This chunk is silent
                        if (consecutiveSilentChunks === 0) {
                            silenceStartTime = chunkStartSec;
                        }
                        consecutiveSilentChunks++;
                    } else {
                        // This chunk has audio - reset counter
                        consecutiveSilentChunks = 0;
                        silenceStartTime = null;
                    }
                }
                
                // Check if we've found enough consecutive silent chunks
                if (consecutiveSilentChunks >= minSilentChunks && silenceStartTime !== null) {
                    return silenceStartTime;
                }
            } catch (err) {
                // Continue to next chunk
            }
        }
        
        // Check if silence extends to the end
        if (consecutiveSilentChunks >= minSilentChunks && silenceStartTime !== null) {
            return silenceStartTime;
        }
        
        return null; // No significant silence region found
    }
    
    /**
     * Backward-compatible sync version that falls back to simple heuristic.
     * Used when scriptClip is not available.
     */
    private scanForGaps() {
        // Simple heuristic fallback - just scan for gaps between words
        const words = this.allWords;
        const newGaps: GapOccurrence[] = [];
        
        for (let i = 0; i < words.length - 1; i++) {
            const currentWord = words[i];
            const nextWord = words[i + 1];
            
            const currentEnd = currentWord.endSec ?? currentWord.startSec;
            const nextStart = nextWord.startSec;
            const gapDuration = nextStart - currentEnd;
            
            if (gapDuration > this.minGapThreshold) {
                const CONTEXT_SECONDS = 2.0;
                const contextStartTime = currentEnd - CONTEXT_SECONDS;
                const contextEndTime = nextStart + CONTEXT_SECONDS;
                
                const contextBefore = words
                    .filter((w, idx) => idx <= i && w.startSec >= contextStartTime)
                    .map(w => w.text);
                const contextAfter = words
                    .filter((w, idx) => idx > i && w.startSec <= contextEndTime)
                    .map(w => w.text);
                
                const gap: GapOccurrence = {
                    id: `gap_${i}_${currentEnd.toFixed(2)}`,
                    durationSec: gapDuration,
                    startSec: currentEnd,
                    endSec: nextStart,
                    srcStartSec: currentWord.srcEndSec ?? currentEnd,
                    srcEndSec: nextWord.srcStartSec ?? nextStart,
                    sourceIndex: currentWord.sourceIndex ?? 0,
                    wordBefore: {
                        text: currentWord.text,
                        startSec: currentWord.startSec,
                        endSec: currentEnd,
                    },
                    wordAfter: {
                        text: nextWord.text,
                        startSec: nextStart,
                        endSec: nextWord.endSec ?? nextStart,
                    },
                    contextBefore,
                    contextAfter,
                };
                
                newGaps.push(gap);
            }
        }
        
        newGaps.sort((a, b) => a.startSec - b.startSec);
        
        runInAction(() => {
            this.gaps = newGaps;
            this.currentIndex = newGaps.length > 0 ? 0 : -1;
        });
    }

    /**
     * Rescan for gaps (after changes).
     */
    async rescan(getTranscript: GetTranscriptFn) {
        this.deactivate();
        await this.activateAsync(getTranscript);
    }
    
    /**
     * Start tracking removal progress.
     */
    startRemoval() {
        this.isRemoving = true;
        this.removalProgress = 0;
    }
    
    /**
     * Update removal progress (0-100).
     */
    updateRemovalProgress(progress: number) {
        this.removalProgress = Math.round(progress);
    }
    
    /**
     * Finish removal progress.
     */
    finishRemoval() {
        this.isRemoving = false;
        this.removalProgress = 100;
    }
    
    /**
     * Get the currently focused gap.
     */
    get currentGap(): GapOccurrence | null {
        if (this.currentIndex >= 0 && this.currentIndex < this.gaps.length) {
            return this.gaps[this.currentIndex];
        }
        return null;
    }
    
    /**
     * Get total count of gaps.
     */
    get totalCount(): number {
        return this.gaps.length;
    }

    /**
     * Count of gaps that would actually be shortened given the current target duration.
     */
    get shortenableCount(): number {
        const target = Number(this.shortenToSeconds);
        if (!Number.isFinite(target) || target < 0) return 0;
        return this.gaps.reduce((acc, gap) => acc + (Number(gap.durationSec) > target ? 1 : 0), 0);
    }

    /**
     * Total seconds that would be removed by "shorten all" with the current target duration.
     */
    get totalSecondsRemoved(): number {
        const target = Number(this.shortenToSeconds);
        if (!Number.isFinite(target) || target < 0) return 0;
        let total = 0;
        for (const gap of this.gaps) {
            const duration = Number(gap.durationSec);
            if (!Number.isFinite(duration)) continue;
            if (duration > target) {
                total += duration - target;
            }
        }
        return total;
    }
    
    /**
     * Get current display index (1-based).
     */
    get currentDisplayIndex(): number {
        return this.gaps.length > 0 ? this.currentIndex + 1 : 0;
    }
    
    /**
     * Go to previous gap.
     */
    goToPrevious() {
        if (this.gaps.length === 0) return;
        this.currentIndex = this.currentIndex > 0 
            ? this.currentIndex - 1 
            : this.gaps.length - 1;
    }
    
    /**
     * Go to next gap.
     */
    goToNext() {
        if (this.gaps.length === 0) return;
        this.currentIndex = (this.currentIndex + 1) % this.gaps.length;
    }
    
    /**
     * Go to specific gap index.
     */
    goToIndex(index: number) {
        if (index >= 0 && index < this.gaps.length) {
            this.currentIndex = index;
        }
    }
    
    /**
     * Shorten current gap and remove from list.
     */
    shortenCurrent(): GapOccurrence | null {
        const current = this.currentGap;
        if (!current) return null;
        
        // Remove from gaps list
        this.gaps = this.gaps.filter(g => g.id !== current.id);
        
        // Adjust index if needed
        if (this.currentIndex >= this.gaps.length) {
            this.currentIndex = Math.max(0, this.gaps.length - 1);
        }
        
        return current;
    }
    
    /**
     * Get all gaps for "shorten all" operation.
     */
    getAllGaps(): GapOccurrence[] {
        return [...this.gaps];
    }
    
    /**
     * Get context words with timestamps for playback preview.
     * Returns words within 2 seconds before and after the gap.
     */
    getContextWordsWithTimestamps(gap: GapOccurrence): {
        wordsBefore: Array<{ text: string; startSec: number; endSec: number }>;
        wordsAfter: Array<{ text: string; startSec: number; endSec: number }>;
    } | null {
        const CONTEXT_SECONDS = 2.0;
        
        // Get words within 2 seconds BEFORE the gap
        const contextStartTime = gap.startSec - CONTEXT_SECONDS;
        const wordsBefore = this.allWords
            .filter(w => w.startSec >= contextStartTime && (w.endSec ?? w.startSec) <= gap.startSec)
            .map(w => ({
                text: w.text,
                startSec: w.startSec,
                endSec: w.endSec ?? w.startSec,
            }));
        
        // Get words within 2 seconds AFTER the gap
        const contextEndTime = gap.endSec + CONTEXT_SECONDS;
        const wordsAfter = this.allWords
            .filter(w => w.startSec >= gap.endSec && w.startSec <= contextEndTime)
            .map(w => ({
                text: w.text,
                startSec: w.startSec,
                endSec: w.endSec ?? w.startSec,
            }));
        
        return { wordsBefore, wordsAfter };
    }
    
    /**
     * Check if a specific gap is the current one.
     */
    isCurrentGap(startSec: number): boolean {
        const current = this.currentGap;
        if (!current) return false;
        const epsilon = 0.01;
        return Math.abs(current.startSec - startSec) < epsilon;
    }
}

// Export singleton instance
export const GapStore = new GapStoreImpl();
